import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  Form,
  Input,
  Switch,
  Select,
  Divider,
  Alert,
  Space,
  Drawer,
  Grid,
  message,
} from "antd";
import {
  UnorderedListOutlined,
  PlusCircleOutlined,
  PictureOutlined,
  OrderedListOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import {
  TaskClassSelector,
  TaskStepEditor,
  TaskStudentsPanel,
  TaskListPanel,
  TaskListView,
  TaskCreateWizard,
} from "./TaskComponent";

import { TeacherTasksAPI } from "../../../api/teacherTasksAPI";
import { QuizApi } from "../../../api/quiz";
import {
  RutasPanel,
  ScenesPanel,
} from "../EvaluationRutasComp/RutasEvaluationList";
import { useTranslation } from "react-i18next";
import { NEW_TAREAS } from "../../../ui/featureFlags";
import { BrandThemeProvider } from "../../../ui";

import styles from "./TeacherTasksGrid.module.scss";
import "./TeacherTasksGrid.scss";

export function TeacherTasksGrid({
  teacher,
  accessToken,
  createOpen = false,
  onCloseCreate,
}) {
  const { t } = useTranslation("tasks");
  const screens = Grid.useBreakpoint();
  const api = useMemo(() => new TeacherTasksAPI(), []);
  const quizApi = useMemo(() => new QuizApi(), []);

  const teacherId = useMemo(
    () => teacher?._id || teacher?.id || null,
    [teacher],
  );
  const institutionId = useMemo(
    () => teacher?.institution?._id || teacher?.institution || null,
    [teacher],
  );

  const initialClass = useMemo(() => {
    return (
      (Array.isArray(teacher?.classrole) &&
        teacher.classrole[0]?.classnumber) ||
      ""
    );
  }, [teacher]);

  // Meta
  const [classnumber, setClassnumber] = useState(initialClass);
  const [title, setTitle] = useState("");
  const [finalQuizRequired, setFinalQuizRequired] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState(null);

  // Quizzes
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizError, setQuizError] = useState("");

  // Rutas/Escenas
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  // Pasos
  const [steps, setSteps] = useState([]);

  // Listado
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // ---------- helpers pasos ----------
  const addSceneAsStep = useCallback(
    (scene) => {
      const sceneId = scene?._id || scene?.id;
      if (!sceneId) return;

      setSteps((arr) => {
        if (arr.some((s) => String(s.scene) === String(sceneId))) return arr;
        const order = arr.length + 1;
        return [
          ...arr,
          {
            scene: sceneId,
            sceneTitle: scene?.title || t("grid.sceneNoTitle"),
            sceneBanner:
              scene?.bannertopUrl ||
              scene?.bannerTopUrl ||
              scene?.banner ||
              null,
            instruction: "",
            order,
          },
        ];
      });
    },
    [t],
  );

  const moveUp = useCallback((i) => {
    setSteps((arr) => {
      if (i <= 0) return arr;
      const next = [...arr];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  }, []);

  const moveDown = useCallback((i) => {
    setSteps((arr) => {
      if (i >= arr.length - 1) return arr;
      const next = [...arr];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  }, []);

  const changeInstruction = useCallback((i, value) => {
    setSteps((arr) => {
      const next = [...arr];
      next[i] = { ...next[i], instruction: value || "" };
      return next;
    });
  }, []);

  const removeStep = useCallback((i) => {
    setSteps((arr) => {
      const next = arr.filter((_, idx) => idx !== i);
      return next.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  }, []);

  // ---------- listado ----------
  const reloadTasks = useCallback(async () => {
    try {
      if (!teacherId) return;
      setLoadingTasks(true);

      const { tasks: raw } = await api.listTeacherTasks(accessToken, teacherId);

      const enriched = await Promise.all(
        (raw || []).map(async (t) => {
          const needs = !t.classnumber || !Array.isArray(t.steps);
          if (!needs) return t;
          try {
            const detail = await api.getTaskById(accessToken, t._id);
            if (detail?.task) {
              return {
                ...t,
                classnumber: detail.task.classnumber,
                steps: detail.task.steps,
              };
            }
          } catch (_) {}
          return t;
        }),
      );

      setTasks(enriched);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error al cargar tareas:", err);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [accessToken, api, teacherId]);

  useEffect(() => {
    reloadTasks();
  }, [reloadTasks]);

  // ---------- quizzes: cargar cuando el toggle se activa ----------
  useEffect(() => {
    let ignore = false;

    const fetchQuizzes = async () => {
      setQuizError("");

      if (!finalQuizRequired) {
        setSelectedQuizId(null);
        setQuizzes([]);
        return;
      }
      if (!teacherId) return;

      setLoadingQuizzes(true);
      try {
        const result = await quizApi.getAllQuizzes(accessToken, teacherId);
        const list = Array.isArray(result?.quizzes)
          ? result.quizzes
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : [];

        if (!ignore) setQuizzes(list);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error cargando quizzes del docente:", e);
        if (!ignore) {
          setQuizzes([]);
          setQuizError(t("grid.quizzesLoadError"));
        }
      } finally {
        if (!ignore) setLoadingQuizzes(false);
      }
    };

    fetchQuizzes();
    return () => {
      ignore = true;
    };
  }, [finalQuizRequired, teacherId, accessToken, quizApi, t]);

  // ---------- crear borrador ----------
  const createDraft = useCallback(async () => {
    setErrorMsg("");
    try {
      if (!teacherId) throw new Error(t("grid.errorNoTeacherId"));
      if (!classnumber) throw new Error(t("grid.errorNoClass"));
      if (!title || !title.trim()) throw new Error(t("grid.errorNoTitle"));
      if (steps.length === 0) throw new Error(t("grid.errorNoSteps"));
      if (finalQuizRequired && !selectedQuizId) {
        throw new Error(t("grid.errorNoQuiz"));
      }

      setCreating(true);

      const payload = api.buildTaskPayload({
        title: title.trim(),
        classnumber,
        steps,
        finalQuizRequired: !!finalQuizRequired,
        finalQuiz: finalQuizRequired && selectedQuizId ? selectedQuizId : null,
        schedule: { startAt: null, endAt: null },
      });

      await api.createTask(accessToken, teacherId, classnumber, payload);

      await reloadTasks();
      setTitle("");
      setFinalQuizRequired(false);
      setSelectedQuizId(null);
      setQuizzes([]);
      setSteps([]);
      if (NEW_TAREAS) onCloseCreate?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setErrorMsg(err?.message || t("grid.draftCreateError"));
    } finally {
      setCreating(false);
    }
  }, [
    accessToken,
    api,
    classnumber,
    finalQuizRequired,
    reloadTasks,
    selectedQuizId,
    steps,
    teacherId,
    title,
    t,
    onCloseCreate,
  ]);

  // ---------- callbacks rutas/escenas ----------
  const handleSelectRoute = useCallback(
    ({ routeId, classnumber: cnFromRoute }) => {
      setSelectedRouteId(routeId || null);
      if (cnFromRoute && !classnumber) setClassnumber(cnFromRoute);
    },
    [classnumber],
  );

  const handleEnterScene = useCallback(
    (scene) => {
      addSceneAsStep(scene);
    },
    [addSceneAsStep],
  );

  const quizOptions = useMemo(() => {
    return (quizzes || [])
      .map((q) => {
        const id = q?._id || q?.id;
        if (!id) return null;
        return {
          value: id,
          label: q?.title || q?.name || `Quiz ${String(id).slice(-6)}`,
        };
      })
      .filter(Boolean);
  }, [quizzes]);

  // Edicion real de borradores: abre el wizard precargado con la tarea.
  const [editTaskId, setEditTaskId] = useState(null);

  // Al abrir "+ Nueva tarea" nos aseguramos de no estar en modo edicion.
  useEffect(() => {
    if (createOpen) setEditTaskId(null);
  }, [createOpen]);

  const onEditTask = useCallback(
    (task) => {
      if (!task?._id) return;
      // Editable en borrador y archivada; bloqueado solo mientras esta activa
      // (hay alumnos trabajando). Para editar una activa: desactivar primero.
      if (task.status === "active") {
        message.info(t("detail.editOnlyInactive"));
        return;
      }
      setEditTaskId(task._id);
    },
    [t],
  );

  const closeCreate = useCallback(() => {
    setEditTaskId(null);
    onCloseCreate?.();
  }, [onCloseCreate]);

  const drawerOpen = createOpen || !!editTaskId;

  // ====================== REDISENO F3 (flag NEW_TAREAS) ======================
  if (NEW_TAREAS) {
    return (
      <BrandThemeProvider>
        <div className={styles.wrap}>
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <UnorderedListOutlined /> {t("grid.myTasksTitle")}
              </span>
            }
          >
            <TaskListView
              accessToken={accessToken}
              tasks={tasks}
              loading={loadingTasks}
              onReload={reloadTasks}
              onEdit={onEditTask}
            />
          </Card>

          <Drawer
            open={drawerOpen}
            onClose={closeCreate}
            title={
              editTaskId ? t("wizard.editTitle") : t("grid.createTaskTitle")
            }
            destroyOnHidden
            styles={{
              wrapper: { width: screens.lg ? 920 : "100%" },
              body: { background: "#ffffff", padding: 20 },
            }}
          >
            <TaskCreateWizard
              teacher={teacher}
              accessToken={accessToken}
              editTaskId={editTaskId}
              onCreated={reloadTasks}
              onClose={closeCreate}
            />
          </Drawer>
        </div>
      </BrandThemeProvider>
    );
  }

  // ====================== LEGACY (flag off) ======================
  return (
    <div className={styles.wrap}>
      <div className={styles.grid}>
        {/* IZQUIERDA */}
        <div className={styles.left}>
          {/* 1) MIS TAREAS */}
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <UnorderedListOutlined /> {t("grid.myTasksTitle")}
              </span>
            }
          >
            <div className={styles.tableWrap}>
              <TaskListPanel
                accessToken={accessToken}
                tasks={tasks}
                loading={loadingTasks}
                onReload={reloadTasks}
              />
            </div>
          </Card>

          {/* 2) CREAR NUEVA TAREA */}
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <PlusCircleOutlined /> {t("grid.createTaskTitle")}
              </span>
            }
          >
            <div className={styles.block}>
              <TaskClassSelector
                teacher={teacher}
                value={classnumber}
                onChange={setClassnumber}
              />
            </div>

            <Form layout="vertical" className={styles.metaForm}>
              <Form.Item label={t("grid.taskTitleLabel")} required>
                <Input
                  size="large"
                  placeholder={t("grid.taskTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Item>

              <Form.Item
                label={t("grid.finalQuizLabel")}
                className={styles.switchRow}
              >
                <Switch
                  checked={finalQuizRequired}
                  onChange={(checked) => {
                    setFinalQuizRequired(checked);
                    if (!checked) {
                      setSelectedQuizId(null);
                      setQuizzes([]);
                    }
                  }}
                />
              </Form.Item>

              {finalQuizRequired ? (
                <Form.Item label={t("grid.selectFinalQuiz")} required>
                  <Select
                    size="large"
                    placeholder={t("grid.selectQuizPlaceholder")}
                    options={quizOptions}
                    value={selectedQuizId || undefined}
                    onChange={(value) => setSelectedQuizId(value || null)}
                    loading={loadingQuizzes}
                    disabled={loadingQuizzes}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                  {quizError ? (
                    <div className={styles.inlineAlert}>
                      <Alert type="warning" showIcon title={quizError} />
                    </div>
                  ) : null}
                </Form.Item>
              ) : null}
            </Form>

            <Divider />

            <div className={styles.tableWrap}>
              <RutasPanel
                teacher={teacherId}
                accessToken={accessToken}
                onSelectRoute={handleSelectRoute}
                hideHeader
              />
            </div>

            {errorMsg ? (
              <div className={styles.inlineAlert}>
                <Alert
                  type="error"
                  showIcon
                  title={t("grid.errorOccurred")}
                  description={errorMsg}
                />
              </div>
            ) : null}
          </Card>

          {/* 3) ESCENAS DE LA RUTA */}
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <PictureOutlined /> {t("grid.scenesTitle")}
              </span>
            }
          >
            {!selectedRouteId ? (
              <Alert type="info" showIcon title={t("grid.selectRouteInfo")} />
            ) : (
              <div className={styles.tableWrap}>
                <ScenesPanel
                  routeId={selectedRouteId}
                  accessToken={accessToken}
                  onEnterScene={handleEnterScene}
                />
              </div>
            )}
          </Card>

          {/* 4) PASOS */}
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <OrderedListOutlined /> {t("grid.stepsTitle")}
              </span>
            }
          >
            <div className={styles.tableWrap}>
              <TaskStepEditor
                steps={steps}
                onMoveUp={moveUp}
                onMoveDown={moveDown}
                onChangeInstruction={changeInstruction}
                onRemove={removeStep}
                onCreateDraft={createDraft}
                creating={creating}
              />
            </div>
          </Card>
        </div>

        {/* DERECHA */}
        <div className={styles.right}>
          <Card
            className={styles.panelCard}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
            title={
              <span className={styles.cardTitle}>
                <TeamOutlined /> {t("grid.studentsTitle")}
              </span>
            }
          >
            <div className={styles.tableWrap}>
              <TaskStudentsPanel
                accessToken={accessToken}
                institutionId={institutionId}
                classnumber={classnumber}
              />
            </div>
          </Card>

          <Space orientation="vertical" size={10} className={styles.helperText}>
            <Typography.Text type="secondary">{t("grid.tip")}</Typography.Text>
          </Space>
        </div>
      </div>
    </div>
  );
}
